// Audience-builder — which geo types Meta/Zernio accept under EXCLUSION.
//
// Zernio's ad-create `excludedLocations` accepts only countries/regions/cities/zips
// (confirmed 2026-06-23). Countries are never user-excludable (you target a country and
// carve areas out of it), so the offerable exclusion set is region / city / zip.
//
// PLACES and NEIGHBORHOODS are NOT accepted under exclusion (Meta itself can't exclude
// them) — the EXCLUDE path omits them so an excluded suburb/neighborhood can't vanish
// without a trace, and warns if legacy/imported data still carries one. CUSTOM-LOCATION
// PINS now ARE accepted under exclusion (Zernio shipped excluded customLocations
// 2026-06-26) — gated by EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED below. Single source of truth
// — shared by the client UI/validation and the server mapper so the two can't diverge.
//
// (When Zernio extends excludedLocations to cover these, add the type here and the whole
// chain — search, flip, map, mapper, reach — picks it up.)

export const EXCLUDABLE_GEO_TYPES = ['region', 'city', 'zip'] as const;
export type ExcludableGeoType = (typeof EXCLUDABLE_GEO_TYPES)[number];

// Custom-location PINS under exclusion — Meta supports excluded custom_locations natively,
// and Zernio now passes them through (excludedLocations.customLocations shipped 2026-06-26).
// HISTORY: this was OFF after a 2026-06-25 live test where an excluded pin published via
// excludedLocations.customLocations was DROPPED entirely on the ad set in Ads Manager
// (Zernio didn't yet expose the field). Zernio has since shipped the fix, so it's RE-ENABLED
// here — pins now EMIT under exclusion via the SAME custom-location builder as the INCLUDE
// path (publish + reach), so the supported types can't diverge. RE-VERIFY on the next test
// publish (Ads Manager: an excluded pin must land on the ad set's targeting, the exact thing
// that dropped before); if it drops again, flip back to false and reopen with Zernio. Places
// + neighborhoods remain un-excludable (Meta itself can't exclude those).
export const EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED = true;

const EXCLUDABLE_SET = new Set<string>(EXCLUDABLE_GEO_TYPES);

// True when a search-result geoType can be EXCLUDED (region/city/zip). False for
// country/place/neighborhood — and any unknown type (fail closed).
export function isExcludableGeoType(geoType: string | null | undefined): boolean {
  return typeof geoType === 'string' && EXCLUDABLE_SET.has(geoType);
}

// The not-offered-for-exclusion message, by the geoType the user tried to exclude.
export function exclusionUnsupportedNote(geoType: string | null | undefined): string {
  if (geoType === 'country') {
    return "Whole countries can't be excluded — exclude a state, city or postcode instead.";
  }
  // place / neighborhood (or anything else not in the excludable search set)
  return "Suburbs and neighborhoods can't be excluded on Meta — exclude a city, state or postcode instead.";
}

// The shape of an excluded bucket, structurally (avoids importing the metaAdDraft type
// into the server reach/publish mappers, which already take loose objects).
type ExcludedBucketLike = {
  places?: unknown[];
  neighborhoods?: unknown[];
  customLocations?: unknown[];
};

// How many excluded entries Meta will DROP at publish — places + neighborhoods always,
// plus custom pins WHILE the gate above is OFF. Drives the publish warning so the drop is
// never silent. (When EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED flips on, excluded pins are
// emitted, so they stop counting here.) Pure + tolerant of loose input.
export function unsupportedExcludedCount(excluded: ExcludedBucketLike | null | undefined): number {
  if (!excluded || typeof excluded !== 'object') return 0;
  const len = (a: unknown): number => (Array.isArray(a) ? a.length : 0);
  const pinsDropped = EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED ? 0 : len(excluded.customLocations);
  return len(excluded.places) + len(excluded.neighborhoods) + pinsDropped;
}
